import api from "../../../api";
import {
  mapPersonalSpaceFile,
  mapPersonalSpaceFolder,
  mapQaItem,
  mapTextItem,
  mapTrainedItem,
  mapUnorganizedItem,
  mapGroupFolder,
  mapGroupItem,
} from "../utils/itemMappers";
import {
  getRepoItemBackendId,
  getRepoItemSource,
} from "../utils/repoItemIdentity";

export async function fetchRepoItems({
  role,
  isPersonalSpaceView,
  statusFilter,
  dayFilter,
  appliedSortOrder,
  signal,
}) {
  let personalSpaceRes = { data: { data: { folders: [], files: [] } } };
  if (isPersonalSpaceView) {
    const endpoint =
      statusFilter === "Saved" ? "/files/get" : "/files/inactive-files";
    const params = {
      page: 1,
      limit: 1000,
      search: "",
      dayFilter: dayFilter === "All" ? "" : dayFilter,
      sortOrder: appliedSortOrder === "Descending" ? "desc" : "asc",
      itemType: "all",
    };
    personalSpaceRes = await api.get(endpoint, { params, signal });
  }

  const [trainedRes, docsRes, textsRes, qaRes] = await Promise.all([
    api.get("/trained-documents/get-trained-docs", { signal }),
    role === "user" || role === "admin"
      ? api.get("/uploaded-files/global-user-docs", { signal })
      : Promise.resolve({ data: { data: [] } }),
    role === "admin"
      ? api.get(`${process.env.REACT_APP_API_URL}/uploaded-files/text`, {
          signal,
        })
      : Promise.resolve({ data: [] }),
    role === "admin"
      ? api.get(`${process.env.REACT_APP_API_URL}/uploaded-files/qa`, {
          signal,
        })
      : Promise.resolve({ data: [] }),
  ]);

  const organizedFilesSet = new Set(
    trainedRes.data
      .filter((item) => item.itemType === "file" && item.uploaded_file_id)
      .map((f) => String(f.uploaded_file_id)),
  );

  const allUserDocs = docsRes.data?.data || docsRes.data || [];
  const unorganizedFiles = allUserDocs.filter(
    (doc) =>
      !organizedFilesSet.has(String(doc.id || doc.file_id)) &&
      !organizedFilesSet.has(String(doc.trained_document_id)),
  );

  const trainedItems = trainedRes.data.map(mapTrainedItem);
  const unorganizedItems = unorganizedFiles.map(mapUnorganizedItem);

  const itemsMap = new Map();
  trainedItems.forEach((item) => itemsMap.set(item.id, item));
  unorganizedItems.forEach((item) => {
    if (!itemsMap.has(item.id)) itemsMap.set(item.id, item);
  });

  const personalSpaceData = personalSpaceRes.data?.data || {
    folders: [],
    files: [],
  };
  const psFolders = personalSpaceData.folders || [];
  const psFiles = personalSpaceData.files || [];

  psFolders.forEach((folder) => {
    const item = mapPersonalSpaceFolder(folder);
    itemsMap.set(item.id, item);
  });

  psFiles.forEach((doc) => {
    const item = mapPersonalSpaceFile(doc);
    itemsMap.set(item.id, item);
  });

  if (role === "admin") {
    (textsRes.data || []).forEach((textItem, index) => {
      const item = mapTextItem(textItem, index);
      itemsMap.set(item.id, item);
    });

    (qaRes.data || []).forEach((qaItem, index) => {
      const item = mapQaItem(qaItem, index);
      itemsMap.set(item.id, item);
    });
  }

  return Array.from(itemsMap.values());
}

export async function createPersonalSpaceFolder(folderName, currentFolder) {
  return api.post("/files/create-folder", {
    folder_name: folderName,
    parent_id: currentFolder ? currentFolder.replace("ps-folder-", "") : null,
    mode: "personal_space",
  });
}

export async function createTrainedDocumentFolder(folderName, currentFolder) {
  return api.post("/trained-documents/create-folder", {
    name: folderName,
    parent_id: currentFolder,
  });
}

export async function renamePersonalSpaceItem(itemId, newName) {
  return api.put("/files/rename", {
    item_id: itemId,
    new_name: newName,
  });
}

export async function renameRepoItem(item, newName) {
  return api.put(`/trained-documents/rename/${getRepoItemBackendId(item)}`, {
    new_name: newName,
    item_source: getRepoItemSource(item),
    user_upload_type: item?.userUploadType || null,
  });
}

export async function markPersonalFileInactive(originalId) {
  return api.put(`/files/mark-inactive/${originalId}`);
}

export async function deletePersonalFile(originalId) {
  return api.delete(`/files/delete/${originalId}`);
}

export async function deleteTrainedDocuments(organizedIds, strategy) {
  return api.delete(`/trained-documents/${organizedIds[0]}`, {
    data: { item_ids: organizedIds, delete_strategy: strategy },
  });
}

export async function deleteUnorganizedDoc(fileId) {
  return api.delete(`/uploaded-files/docs/${fileId}`);
}

/**
 * Proxy to the Python service POST /delete_uploaded_file/.
 * Deletes all vectors + the associated uploaded file for a document.
 * Must succeed BEFORE deleting PG records / physical files.
 */
export async function deleteUploadedFileFromPython({
  document_id,
  python_file_uuid,
  uploaded_file_id,
  user_files_id,
  enterprise_id,
  user_id,
  mode,
}) {
  return api.post("/uploaded-files/delete-uploaded-file", {
    document_id,
    python_file_uuid,
    uploaded_file_id,
    user_files_id,
    enterprise_id,
    user_id,
    ...(mode ? { mode } : {}),
  });
}

/**
 * Proxy to the Python service POST /content-delete/.
 * Deletes Pinecone chunks for web_url / video / text / qa content.
 */
export async function deleteContentFromPython({
  document_id,
  enterprise_id,
  content_type,
}) {
  return api.post("/uploaded-files/content-delete", {
    document_id,
    enterprise_id,
    content_type,
  });
}

/** Permanently delete a text/Q&A record (UserUpload) by its upload_id. */
export async function deleteUserUploadRecord(uploadId) {
  return api.delete(`/uploaded-files/user-upload/${uploadId}`);
}

/**
 * Proxy to the Python service POST /approve_document.
 * Promotes an isolated document to enterprise-wide visibility by patching
 * vector metadata in-place (no re-embedding required).
 */
export async function approveDocumentFromPython({
  document_id,
  enterprise_id,
  approved_by,
}) {
  return api.post("/uploaded-files/approve-document", {
    document_id,
    enterprise_id,
    approved_by,
  });
}

/**
 * Proxy to the Python service POST /reject_document.
 * Rejects a document by deleting its vectors and local file artifacts, then
 * marking the review record as rejected.
 */
export async function rejectDocumentFromPython({
  document_id,
  enterprise_id,
  rejected_by,
  reason,
}) {
  return api.post("/uploaded-files/reject-document", {
    document_id,
    enterprise_id,
    rejected_by,
    reason,
  });
}

export async function downloadPersonalFile(originalId) {
  return api.get("/files/download", {
    params: { user_files_id: originalId },
    responseType: "blob",
  });
}

export async function downloadTrainedDocument(idToUse) {
  return api.get(`/trained-documents/download/${idToUse}`, {
    responseType: "blob",
  });
}

export async function bulkMovePersonalItems(itemIds, targetParentId) {
  return api.put("/files/bulk-move", {
    item_ids: itemIds,
    target_parent_id: targetParentId,
  });
}

export async function moveUnorganizedFiles(
  unorganizedIds,
  targetFolderId,
  departmentId = null,
) {
  return api.put("/trained-documents/move-uploaded-files", {
    uploaded_file_ids: unorganizedIds,
    target_folder_id: targetFolderId,
    ...(departmentId ? { department_id: departmentId } : {}),
  });
}

export async function moveOrganizedItems(
  organizedItems,
  targetFolderId,
  departmentId = null,
) {
  return api.put("/trained-documents/move-items", {
    items: organizedItems,
    target_folder_id: targetFolderId,
    ...(departmentId ? { department_id: departmentId } : {}),
  });
}

export async function revertPersonalFile(originalId) {
  return api.put(`/files/revert/${originalId}`);
}

export async function updateFileStatus(fileId, fileType, newStatus) {
  if (fileType === "qa" || fileType === "text") {
    return api.patch(`/uploaded-files/update_qa_status/${fileId}`, {
      upload_status: newStatus,
    });
  }

  return api.patch(`/uploaded-files/update_file_status/${fileId}`);
}

export async function updateApprovalStatus(fileId, approvalStatus) {
  return api.post("/uploaded-files/approve-or-reject-File", {
    file_id: fileId,
    approval_status: approvalStatus,
    notes: "",
  });
}

export async function uploadPersonalFiles(formData) {
  return api.post("/files/upload-multiple", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function downloadFileForTraining(originalId) {
  return api.get("/files/download", {
    params: { user_files_id: originalId },
    responseType: "blob",
  });
}

export async function uploadForTraining(formData) {
  return api.post("/uploaded-files/newQueryUpload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export function triggerBlobDownload(blob, fileName) {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ============================================================
// GROUP REPOSITORY FUNCTIONS
// ============================================================

/**
 * Fetch all enterprise groups visible to the current user.
 * Users get only their groups (/departments/accessible);
 * admins get all groups (/departments).
 */
export async function fetchEnterpriseGroups({ role, signal } = {}) {
  try {
    // /departments/accessible returns only groups the user belongs to.
    // Falls back to /departments for admins or when the endpoint is unavailable.
    const endpoint =
      role === "admin" ? "/departments" : "/departments/accessible";
    const res = await api.get(endpoint, { signal, skipLoader: true });
    const raw = Array.isArray(res.data) ? res.data : [];
    return raw
      .map((g) => ({
        id: String(g.department_id ?? g.id ?? ""),
        name: String(g.department_name ?? g.name ?? ""),
        updatedAt: g.updatedAt || g.updated_at || null,
        createdAt: g.createdAt || g.created_at || null,
      }))
      .filter((g) => g.id && g.name);
  } catch (err) {
    if (
      err.name === "AbortError" ||
      err.name === "CanceledError" ||
      err.code === "ERR_CANCELED"
    )
      throw err;
    console.error("Failed to fetch enterprise groups:", err);
    return [];
  }
}

/**
 * Fetch all items (folders + files) for a specific group.
 * Returns mapped items with groupId attached.
 */
export async function fetchGroupItems({ groupId, signal } = {}) {
  const res = await api.get("/trained-documents/get-trained-docs", {
    params: { department_id: groupId },
    signal,
    skipLoader: true,
  });
  const raw = Array.isArray(res.data) ? res.data : [];
  return raw.map((item) => mapGroupItem(item, groupId));
}

/**
 * Fetch all groups + (optionally) the contents of a specific group.
 * Returns a flat item list where group root folders are at parentId=null
 * and group contents are nested under their group folder.
 */
export async function fetchGroupRepoItems({
  role,
  selectedGroupId,
  signal,
} = {}) {
  const groups = await fetchEnterpriseGroups({ role, signal });
  const groupRootFolders = groups.map(mapGroupFolder);

  if (!selectedGroupId) {
    return groupRootFolders;
  }

  // Fetch actual folder/file contents for the selected group
  const groupContents = await fetchGroupItems({
    groupId: selectedGroupId,
    signal,
  });

  return [...groupRootFolders, ...groupContents];
}

/**
 * Create a sub-folder inside a group.
 */
export async function createGroupFolder(groupId, folderName, parentFolderId) {
  return api.post("/trained-documents/create-folder", {
    name: folderName,
    parent_id: parentFolderId,
    department_id: groupId,
  });
}

/**
 * Download a file item that belongs to a group.
 */
export async function downloadGroupFile(item) {
  const idToUse = item.uploaded_file_id || item.backendId || item.id;
  return api.get(`/trained-documents/download/${idToUse}`, {
    responseType: "blob",
  });
}

/**
 * Update file status for a group file.
 */
export async function updateGroupFileStatus(fileId, fileType, newStatus) {
  return updateFileStatus(fileId, fileType, newStatus);
}

/**
 * Move items within the same group to a target folder.
 * Items must all share the same groupId as the target.
 */
export async function moveGroupItems(
  organizedItems,
  unorganizedIds,
  targetFolderId,
) {
  return Promise.all([
    unorganizedIds.length > 0
      ? moveUnorganizedFiles(unorganizedIds, targetFolderId)
      : Promise.resolve(),
    organizedItems.length > 0
      ? moveOrganizedItems(organizedItems, targetFolderId)
      : Promise.resolve(),
  ]);
}

/**
 * Rename an item inside a group — delegates to existing rename logic.
 */
export async function renameGroupItem(item, newName) {
  return renameRepoItem(item, newName);
}
